/**
 * TenantModulesTab.tsx — F7a T7.4.
 *
 * Zakładka "Moduły" na stronie AdminTenantDetail. Pokazuje pełen katalog
 * modułów zmergowany z subskrypcjami tenanta; button Aktywuj / Deaktywuj per
 * wiersz (disabled + tooltip dla modułów core).
 *
 * Data:
 *   - GET /api/admin/modules (catalog, staleTime 5min)
 *   - GET /api/admin/tenants/:id/modules (subskrypcje tenanta)
 * Obie zwrotki mergowane w kliencie po `code`.
 *
 * Mutacje: inline via modale (Activate / Deactivate); na sukces invalidate obu
 * queries + queries strony detail (Overview też pokazuje subscriptions).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { ActivateBundleModal, type BundleActivationSummary } from './ActivateBundleModal';
import { ActivateModuleModal } from './ActivateModuleModal';
import { DeactivateModuleModal } from './DeactivateModuleModal';

interface CatalogModule {
  code: string;
  name: string;
  isCore: boolean;
  category: string | null;
  icon: string | null;
  sortOrder: number | null;
}

interface TenantSubscription {
  code: string;
  name: string;
  isCore: boolean;
  status: 'trialing' | 'active' | 'past_due' | 'suspended' | 'cancelled' | null;
  trialEndsAt: string | null;
  activatedAt: string | null;
  cancelledAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  seats: number | null;
  priceSnapshotMonthly: number | null;
  priceSnapshotYearly: number | null;
}

interface CatalogResponse {
  modules: CatalogModule[];
}

interface SubscriptionsResponse {
  tenant: { id: string; slug: string; code: string };
  subscriptions: TenantSubscription[];
}

type MergedStatus = 'trialing' | 'active' | 'past_due' | 'suspended' | 'cancelled' | 'none';

interface Row {
  code: string;
  name: string;
  isCore: boolean;
  status: MergedStatus;
  trialEndsAt: string | null;
  activatedAt: string | null;
  seats: number | null;
}

const STATUS_TO_COLOR: Record<string, BadgeColor> = {
  active: 'green',
  trialing: 'blue',
  past_due: 'amber',
  cancelled: 'gray',
  suspended: 'red',
  none: 'gray',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface TenantModulesTabProps {
  tenantId: string;
}

export function TenantModulesTab({ tenantId }: TenantModulesTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [activateTarget, setActivateTarget] = useState<Row | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Row | null>(null);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);

  const [catalogQuery, subsQuery] = useQueries({
    queries: [
      {
        queryKey: ['admin', 'modules', 'catalog'],
        queryFn: async (): Promise<CatalogResponse> => {
          const res = await api.get<CatalogResponse>('/admin/modules');
          return res.data;
        },
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['admin', 'tenants', tenantId, 'modules'],
        queryFn: async (): Promise<SubscriptionsResponse> => {
          const res = await api.get<SubscriptionsResponse>(`/admin/tenants/${tenantId}/modules`);
          return res.data;
        },
      },
    ],
  });

  const isLoading = catalogQuery.isLoading || subsQuery.isLoading;
  const isError = catalogQuery.isError || subsQuery.isError;

  const rows = useMemo<Row[]>(() => {
    const catalog = catalogQuery.data?.modules ?? [];
    const subs = subsQuery.data?.subscriptions ?? [];
    const subByCode = new Map(subs.map((s) => [s.code, s]));
    return catalog.map((mod) => {
      const sub = subByCode.get(mod.code);
      const status: MergedStatus = sub?.status ?? 'none';
      return {
        code: mod.code,
        name: mod.name,
        isCore: mod.isCore,
        status,
        trialEndsAt: sub?.trialEndsAt ?? null,
        activatedAt: sub?.activatedAt ?? null,
        seats: sub?.seats ?? null,
      };
    });
  }, [catalogQuery.data, subsQuery.data]);

  const statusLabel = (status: MergedStatus): string => {
    if (status === 'none') return t('admin.tenants.detail.modules.status.none');
    const key = `admin.tenants.subscriptionStatus.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const periodLabel = (row: Row): string => {
    if (row.status === 'trialing' && row.trialEndsAt) {
      return t('admin.tenants.detail.modules.periodLabel.trial', {
        date: formatDate(row.trialEndsAt),
      });
    }
    if (row.status === 'active' && row.activatedAt) {
      return t('admin.tenants.detail.modules.periodLabel.active', {
        date: formatDate(row.activatedAt),
      });
    }
    return t('admin.tenants.detail.modules.periodLabel.none');
  };

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', tenantId, 'modules'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
  }

  const statusFilterOptions = useMemo(
    () =>
      (['none', 'trialing', 'active', 'past_due', 'suspended', 'cancelled'] as MergedStatus[]).map(
        (s) => ({ value: s, label: statusLabel(s) }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const columns = useMemo<ColumnDefinition<Row>[]>(
    () => [
      {
        id: 'code',
        accessor: 'code',
        header: t('admin.tenants.detail.modules.columns.code'),
        sortable: true,
        renderCell: (value) => <span className="font-mono text-xs">{String(value)}</span>,
      },
      {
        id: 'name',
        accessor: 'name',
        header: t('admin.tenants.detail.modules.columns.name'),
        sortable: true,
        renderCell: (_value, row) => (
          <span className="font-medium text-text-primary">{row.name}</span>
        ),
      },
      {
        id: 'status',
        accessor: (row) => row.status,
        header: t('admin.tenants.detail.modules.columns.status'),
        sortable: true,
        // Stany rozłączne → dropdown wyboru (client-side, exact-match).
        headerFilter: { category: 'select', options: statusFilterOptions },
        renderCell: (_value, row) => (
          <Badge color={STATUS_TO_COLOR[row.status] ?? 'gray'}>{statusLabel(row.status)}</Badge>
        ),
      },
      {
        id: 'period',
        // Surowa data okresu (koniec triala / data aktywacji) — baza dla filtra i sortowania;
        // etykieta pochodna („Trial do…") renderowana osobno w renderCell.
        accessor: (row) =>
          row.status === 'trialing'
            ? row.trialEndsAt
            : row.status === 'active'
              ? row.activatedAt
              : null,
        header: t('admin.tenants.detail.modules.columns.period'),
        sortable: true,
        // Filtr zakresu dat (kontrolka daty ConfigurableTable) — spójnie z „Utworzono" w Pulach baz.
        headerFilter: { category: 'date' },
        renderCell: (_value, row) => (
          <span className="text-xs text-text-secondary">{periodLabel(row)}</span>
        ),
      },
      {
        id: 'seats',
        accessor: (row) => row.seats ?? '',
        header: t('admin.tenants.detail.modules.columns.seats'),
        sortable: true,
        filterable: false,
        align: 'right',
        renderCell: (_value, row) =>
          row.seats === null ? (
            <span className="text-text-muted">—</span>
          ) : (
            <span>{row.seats}</span>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, statusFilterOptions],
  );

  const renderActions = (row: Row) => {
    const isActive = row.status === 'active' || row.status === 'trialing';
    const canActivate = !isActive;
    const canDeactivate = isActive && !row.isCore;
    return (
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={!canActivate}
          onClick={() => setActivateTarget(row)}
        >
          {t('admin.tenants.detail.modules.actions.activate')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!canDeactivate}
          title={
            row.isCore
              ? t('admin.tenants.detail.modules.actions.deactivateDisabledTooltip')
              : undefined
          }
          onClick={() => setDeactivateTarget(row)}
        >
          {t('admin.tenants.detail.modules.actions.deactivate')}
        </Button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={48} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
      >
        <p>{t('admin.tenants.detail.modules.loadError')}</p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() => {
            void catalogQuery.refetch();
            void subsQuery.refetch();
          }}
        >
          {t('admin.tenants.detail.modules.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            {t('admin.tenants.detail.modules.title')}
          </h3>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {t('admin.tenants.detail.modules.subtitle')}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setBundleModalOpen(true)}>
          {t('admin.tenants.detail.modules.activateBundleModal.openButton')}
        </Button>
      </div>

      <PreviewTenantAuthBoundary>
        <ConfigurableTable<Row>
          tableId="admin-tenant-modules"
          columns={columns}
          data={rows}
          getRowId={(row) => row.code}
          renderActions={renderActions}
          actionsColumnLabel={t('admin.tenants.detail.modules.columns.actions')}
          persistConfig={false}
          showViewModeToggle={false}
          emptyTitle={t('admin.tenants.detail.modules.empty')}
        />
      </PreviewTenantAuthBoundary>

      {activateTarget && (
        <ActivateModuleModal
          open={!!activateTarget}
          tenantId={tenantId}
          moduleCode={activateTarget.code}
          moduleName={activateTarget.name}
          onClose={() => setActivateTarget(null)}
          onSuccess={() => {
            const name = activateTarget.name;
            setActivateTarget(null);
            invalidateAll();
            toast.show({
              variant: 'success',
              title: t('admin.tenants.detail.modules.toasts.activateSuccess', { module: name }),
            });
          }}
        />
      )}

      {deactivateTarget && (
        <DeactivateModuleModal
          open={!!deactivateTarget}
          tenantId={tenantId}
          moduleCode={deactivateTarget.code}
          moduleName={deactivateTarget.name}
          onClose={() => setDeactivateTarget(null)}
          onSuccess={() => {
            const name = deactivateTarget.name;
            setDeactivateTarget(null);
            invalidateAll();
            toast.show({
              variant: 'success',
              title: t('admin.tenants.detail.modules.toasts.deactivateSuccess', { module: name }),
            });
          }}
        />
      )}

      {bundleModalOpen && (
        <ActivateBundleModal
          open={bundleModalOpen}
          tenantId={tenantId}
          onClose={() => setBundleModalOpen(false)}
          onSuccess={(summary: BundleActivationSummary) => {
            setBundleModalOpen(false);
            invalidateAll();
            toast.show({
              variant: 'success',
              title: t('admin.tenants.detail.modules.toasts.activateBundleSuccess', {
                bundle: summary.bundleName,
                activated: summary.activated.length,
                skipped: summary.skipped.length,
              }),
            });
          }}
        />
      )}
    </div>
  );
}
